/**
 * Video pane for /game-match-summary/<game_code> — port of the template's
 * video container markup (~lines 1735-1830), loadVideo/handleVideoPlay/
 * handleVideoPause/handleVideoSeeked (~5479-5663), playMoment's video half
 * (~4005-4030), the upload placeholder + VideoAttachClient wiring
 * (~5534-5593), the match clock (updateMatchClock ~7116), keyboard
 * shortcuts (~6169-6222), and the Video Sync Settings modal
 * (markup ~1896-1953, logic ~7623-7695).
 *
 * Pass 11 wired the canvas subsystems in: the pitch-canvas controller drives
 * playback sync (handleVideoPlay/Pause/Seeked canvas halves, playMoment's
 * unified-load sequence), the telestration controls render (~1789-1830 —
 * offside/telestrate/backline/midfield/shape/space/detection/clear; the QA
 * banner buttons are email-gated internal QA and excluded with the banners),
 * and the ?overlays= detection deep link auto-enables.
 *
 * Pass 12 wired the mark-a-moment panel (template toggleMarkMoment /
 * setMomentStart / setMomentEnd / saveManualMoment, ~3215-3322): scissors
 * button toggles the panel, Set-to-now reads the current video frame, save
 * POSTs /game-match-summary/api/manual-moments and hands the created moment
 * to the parent (the template appended it locally instead of refetching,
 * PRI-1401).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  frameToTime,
  frameToVideoTime,
  timeToFrame,
  videoTimeToFrame,
  formatGameTime,
  stepFrameSequence,
  type StepAnchor,
  type SyncState,
} from './frameTime'
import { VideoAttachClient } from './videoAttach'
import {
  findSkipTarget,
  nextSegmentStart,
  prevSegmentStart,
  type PlaybackSegment,
} from './playbackSegments'
import { structureMarks } from './timelineMarks'
import { TransportControls } from './TransportControls'
import { useSession } from '../../lib/session'
import type { CanvasController } from './canvasController'
import type { GameData, Moment, TeamIdentity } from './types'

// Template showToast (same helper lives in GameMatchSummary; duplicated here
// to avoid a circular import between the page and this component).
function showToast(message: string) {
  const toast = document.createElement('div')
  toast.className = 'video-toast'
  toast.textContent = message
  document.body.appendChild(toast)
  setTimeout(() => toast.remove(), 2100)
}

export interface VideoPlayerHandle {
  /** Template playMoment's video half: seek 3s before the frame and play. */
  playMoment: (frame: number) => void
  /** Template seekToFrame: plain seek without autoplay. */
  seekToFrame: (frame: number) => void
  /** Template getCurrentFrame. */
  getCurrentFrame: () => number
  /** Pause playback — frame-adjust needs a still video to step against. */
  pause: () => void
  /** The <video> itself, read at call time — for controls outside the
   *  player that read or set its clock (calibration's match marks). */
  getVideo: () => HTMLVideoElement | null
}

interface VideoPlayerProps {
  gameCode: string
  videoUrl: string | null
  /** The proxied 302 route. The direct presigned URL above saves a round
   *  trip, but depends on the bucket answering CORS for this origin
   *  (measured true 2026-09-25); if the media errors, the player swaps to
   *  this once — exactly the pre-fastpath behaviour, never a black box. */
  videoUrlFallback?: string | null
  sync: SyncState
  gameData: GameData | null
  /** ?frame= deep link — consumed once on loadedmetadata, as the template did. */
  deepLinkFrame: number | null
  showFrameCounter: boolean
  /** Pass 11: the pitch-canvas controller (null until the page effect creates it). */
  canvas: CanvasController | null
  hasTelestration: boolean
  annotationMode: boolean
  /** Detection button is staff-gated (was an email allowlist; 2026-08-31). */
  showDetectionBtn: boolean
  telLayers: Record<string, boolean>
  detectionActive: boolean
  highlightMode: 'off' | 'selecting' | 'active'
  highlightBall: boolean
  highlightLinesVideo: boolean
  highlightRingsSpatial: boolean
  highlightLinesSpatial: boolean
  /** ?overlays= deep link (QA) — only 'detection' is live this pass. */
  deepLinkOverlays: string | null
  /** 'h' hotkey — toggles the spatial-control canvas layer. */
  onSpatialHotkey: () => void
  /** Pass 12: a manual moment was saved — parent appends it to its list. */
  onManualMomentCreated: (moment: Moment) => void
  onVideoAttached: (videoUrl: string) => void
  onSyncSaved: (vals: {
    video_game_start_time: number
    video_halftime_start_time: number | null
    video_halftime_end_time: number | null
    video_normaltime_end_time: number | null
    /** Absent when the reviewer saved without choosing one. */
    home_team_start_direction?: string
  }) => void
  onShareCurrentFrame: (frame: number) => void
  /** Pop the video pane onto another monitor; absent where unsupported. */
  onPopOut?: () => void
  poppedOut?: boolean
  /** False hides the overlay chips sitting on the video (founder ask). */
  showActions?: boolean
  /**
   * Fires on every 'timeupdate' with the current game frame. Deliberately a
   * callback rather than lifted state: this runs ~4x a second and the parent
   * must not re-render the page on it. `seeked` marks a deliberate jump.
   */
  onFrameTick?: (frame: number, seeked?: boolean) => void
  /**
   * Filtered playback: when set, playback skips any gap between these
   * segments (a seek, not a pause) and pauses after the last one. Null turns
   * the mode off. Only acts while playing, so a paused scrub is never yanked.
   */
  skipSegments?: PlaybackSegment[] | null
  /** Running scoreline at the playhead ("LAG 0–1 SKC"), from confirmed
   *  goals — rendered beside the match clock. Null/absent renders
   *  nothing (an unknown score is not 0-0). */
  scoreline?: string | null
}

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer(
  {
    gameCode, videoUrl, videoUrlFallback = null, sync, gameData, deepLinkFrame, showFrameCounter,
    canvas, hasTelestration, annotationMode, showDetectionBtn, telLayers,
    detectionActive, highlightMode, highlightBall,
    highlightLinesVideo, highlightRingsSpatial, highlightLinesSpatial,
    deepLinkOverlays, onSpatialHotkey, onManualMomentCreated,
    onVideoAttached, onSyncSaved, onShareCurrentFrame, onPopOut, poppedOut,
    onFrameTick, skipSegments = null, showActions = true, scoreline = null,
  },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const clockRef = useRef<HTMLDivElement>(null)
  const [videoDuration, setVideoDuration] = useState(0)
  /** Mirrors the element's paused state: the marks strip pins itself
   *  visible whenever the native controls are (paused shows them). */
  const [videoPaused, setVideoPaused] = useState(true)
  /** True after the direct src errored; the element then plays the
   *  proxied fallback. Reset when a new URL arrives (new game, re-attach). */
  const [srcFailed, setSrcFailed] = useState(false)
  useEffect(() => { setSrcFailed(false) }, [videoUrl])
  const fallbackRef = useRef<string | null>(null)
  fallbackRef.current = videoUrlFallback ?? null
  const frameRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  /** The frame the LAST arrow press targeted — stepFrameSequence continues
   *  from it while the element merely echoes that seek back. */
  const stepAnchorRef = useRef<StepAnchor | null>(null)

  // Refs mirror props so the once-attached video/document listeners never go
  // stale (the template read module globals; this is the React equivalent).
  const syncRef = useRef(sync)
  syncRef.current = sync
  const frameTickRef = useRef(onFrameTick)
  frameTickRef.current = onFrameTick
  const canvasRef = useRef(canvas)
  canvasRef.current = canvas
  const spatialHotkeyRef = useRef(onSpatialHotkey)
  spatialHotkeyRef.current = onSpatialHotkey
  const skipSegmentsRef = useRef(skipSegments)
  skipSegmentsRef.current = skipSegments
  const deepLinkRef = useRef<number | null>(deepLinkFrame)
  useEffect(() => {
    if (deepLinkFrame != null) deepLinkRef.current = deepLinkFrame
  }, [deepLinkFrame])
  // A seek initiated by playMoment loads its own data and starts playback;
  // reloading in the seeked handler would race that load (template
  // syncState.suppressNextSeekReload).
  const suppressNextSeekReload = useRef(false)

  // Telestration controls panel visibility: per-user preference persisted in
  // localStorage; default hidden — the user opts in via the eye toggle
  // (template loadGameData + toggleTelControlsVisibility).
  const [telControlsVisible, setTelControlsVisible] = useState(
    () => localStorage.getItem('tel_controls_visible') === '1',
  )
  // Template exitAnnotationMode set the panel back to display:flex
  // unconditionally (without touching localStorage).
  const prevAnnotationMode = useRef(annotationMode)
  useEffect(() => {
    if (prevAnnotationMode.current && !annotationMode) setTelControlsVisible(true)
    prevAnnotationMode.current = annotationMode
  }, [annotationMode])

  const toggleTelControlsVisibility = () => {
    setTelControlsVisible((showing) => {
      localStorage.setItem('tel_controls_visible', showing ? '0' : '1')
      return !showing
    })
  }

  // ?overlays= deep link (template DOMContentLoaded tail): only detection is
  // live this pass — the QA possession/chain banners are deferred with the
  // banners themselves.
  const overlaysConsumed = useRef(false)
  useEffect(() => {
    if (!canvas || overlaysConsumed.current || !deepLinkOverlays) return
    overlaysConsumed.current = true
    const wanted = deepLinkOverlays.split(',').map((s) => s.trim())
    if (wanted.includes('detection') && showDetectionBtn && !canvas.telestration.detectionActive) {
      canvas.telestration.toggleDetectionBoxes()
    }
    // Reveal the telestration controls panel if it was hidden.
    setTelControlsVisible(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas])

  const [upload, setUpload] = useState<{
    visible: boolean
    title: string
    percent: number
    status: string
    failed: boolean
  }>({ visible: false, title: 'Uploading video…', percent: 0, status: '0%', failed: false })

  const [syncModalOpen, setSyncModalOpen] = useState(false)

  const getCurrentFrame = useCallback((): number => {
    const video = videoRef.current
    if (!video || isNaN(video.currentTime)) return 0
    return videoTimeToFrame(syncRef.current, video.currentTime)
  }, [])

  // -------------------------------------------------------------------------
  // Mark a moment (Pass 12 — template markMomentState + panel, ~3215-3322)
  // -------------------------------------------------------------------------
  const [markMoment, setMarkMoment] = useState<{
    active: boolean
    startFrame: number | null
    endFrame: number | null
  }>({ active: false, startFrame: null, endFrame: null })
  const [mmStart, setMmStart] = useState('')
  const [mmEnd, setMmEnd] = useState('')
  const [mmTitle, setMmTitle] = useState('')

  const cancelMarkMoment = () => {
    setMarkMoment({ active: false, startFrame: null, endFrame: null })
  }

  const toggleMarkMoment = () => {
    if (markMoment.active) {
      cancelMarkMoment()
      return
    }
    const startFrame = getCurrentFrame()
    setMarkMoment({ active: true, startFrame, endFrame: null })
    setMmStart(frameToTime(startFrame, syncRef.current.fps))
    setMmEnd('')
    setMmTitle('')
  }

  const setMomentStart = () => {
    const startFrame = getCurrentFrame()
    setMarkMoment((m) => ({ ...m, startFrame }))
    setMmStart(frameToTime(startFrame, syncRef.current.fps))
  }

  const setMomentEnd = () => {
    const endFrame = getCurrentFrame()
    setMarkMoment((m) => ({ ...m, endFrame }))
    setMmEnd(frameToTime(endFrame, syncRef.current.fps))
  }

  const saveManualMoment = async () => {
    const title = mmTitle.trim() || 'Manual moment'

    // Read from the input fields (user may have typed a time or used "Set to now")
    const fps = syncRef.current.fps
    const startFrame = timeToFrame(mmStart, fps) ?? markMoment.startFrame
    const endFrame = timeToFrame(mmEnd, fps) ?? markMoment.endFrame ?? startFrame

    if (startFrame == null) {
      showToast('Set a start time first')
      return
    }

    try {
      const resp = await fetch(`/api/v1/game-match-summary/manual-moments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          game_code: gameCode,
          frame: startFrame,
          start_frame: startFrame,
          end_frame: endFrame,
          title: title,
        }),
      })

      if (resp.ok) {
        showToast('Moment saved')
        cancelMarkMoment()
        // Append the created moment locally instead of refetching and
        // re-parsing the entire moments array (PRI-1401)
        try {
          const data = (await resp.json()) as { success?: boolean; moment?: Moment }
          if (data.success && data.moment) {
            onManualMomentCreated(data.moment)
          }
        } catch (e) {
          console.error('Failed to append moment:', e)
        }
      } else {
        showToast('Failed to save moment')
      }
    } catch (err) {
      console.error('Save manual moment failed:', err)
      showToast('Failed to save moment')
    }
  }

  // Template playMoment's video half (~4005-4056): stop the canvas loop,
  // pause + seek, unified-load tracking/spatial/formations for the start
  // frame, then play with the canvas loop running in sync.
  const playMoment = useCallback((frame: number) => {
    const video = videoRef.current
    if (!video || !videoUrl) return
    const cv = canvasRef.current
    // Start from 3 seconds (90 frames at 30fps) before the moment
    const startFrame = Math.max(0, frame - 90)
    const videoTime = frameToVideoTime(syncRef.current, startFrame)

    // Stop any existing canvas playback and reset loading state
    cv?.stopCanvasPlayback()
    if (cv) cv.state.isLoadingFrames = false // allow new load

    // Pause video and seek to position. This seek's data load happens below;
    // tell the seeked handler not to start a competing reload.
    video.pause()
    suppressNextSeekReload.current = true
    video.currentTime = videoTime

    void (async () => {
      try {
        // Load all data using unified loading
        await cv?.loadAllDataUnified(startFrame, true)

        // Start synchronized playback
        await video.play()

        // Start the canvas loop directly instead of relying on the play
        // event's buffer check, which can miss if the buffer changed during
        // loading
        if (cv && !cv.state.isPlaying) {
          cv.startCanvasPlayback()
        }
        // If the seeked event never fired (e.g. already at the target time),
        // don't let the suppress flag swallow a future user seek
        suppressNextSeekReload.current = false
      } catch (error) {
        console.error('[playMoment] Error during loading or playback:', error)
        console.warn('[playMoment] Playback may be out of sync due to loading failure')
        suppressNextSeekReload.current = false

        // Still try to play the video even if loading failed
        try {
          await video.play()
          if (cv && !cv.state.isPlaying) {
            cv.startCanvasPlayback()
          }
        } catch (playError) {
          console.error('[playMoment] Failed to start video playback:', playError)
        }
      }
    })()
  }, [videoUrl])

  const seekToFrame = useCallback((frame: number) => {
    const video = videoRef.current
    if (video) video.currentTime = frameToVideoTime(syncRef.current, frame)
    if (canvasRef.current?.state.canvas) {
      void canvasRef.current.loadFrameData(frame, false)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    playMoment, seekToFrame, getCurrentFrame,
    pause: () => videoRef.current?.pause(),
    getVideo: () => videoRef.current,
  }), [playMoment, seekToFrame, getCurrentFrame])

  // Video sync listeners — template loadVideo attached these once.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onPlay = () => {
      setVideoPaused(false)
      canvasRef.current?.handleVideoPlay(video)
    }
    const onPause = () => {
      setVideoPaused(true)
      canvasRef.current?.handleVideoPause()
    }
    const onSeeked = () => {
      // A deliberate jump through the match is a stronger signal of intent
      // than the moments rail's follow lock — tell the page so the rail
      // re-syncs even if the reader had scrolled away earlier.
      frameTickRef.current?.(videoTimeToFrame(syncRef.current, video.currentTime), true)
      // A seek initiated by playMoment loads its own data and starts
      // playback; reloading here would race that load. Consume the flag.
      if (suppressNextSeekReload.current) {
        suppressNextSeekReload.current = false
        return
      }
      void canvasRef.current?.handleVideoSeeked(video)
    }
    // Blur video on click so spacebar/arrow shortcuts route through document
    // handler instead of the video element's internal native-controls
    // keyboard handling.
    const onClick = () => video.blur()
    // Muted by default (Ben, 2026-09-01). Set the property too: React only
    // renders the attribute, which browsers ignore after hydration quirks.
    video.defaultMuted = true
    video.muted = true
    // Update clock on timeupdate (separate from canvas animation to ensure
    // clock always works)
    const onTimeUpdate = () => {
      const videoFrame = videoTimeToFrame(syncRef.current, video.currentTime)
      if (clockRef.current) {
        clockRef.current.textContent = formatGameTime(videoFrame, syncRef.current.fps)
      }
      if (frameRef.current) {
        frameRef.current.textContent = `f${videoFrame}`
      }
      frameTickRef.current?.(videoFrame)

      // Filtered playback: while playing, skip any gap between segments.
      // Playing-only, so a paused scrub is never yanked; the half-second
      // tolerance absorbs seek-landing rounding (see findSkipTarget).
      // An empty list (filters match nothing) plays normally — the toolbar
      // count says why — rather than pausing on a misleading "end".
      const segs = skipSegmentsRef.current
      if (segs && segs.length > 0 && !video.paused && !video.seeking) {
        const fps = syncRef.current.fps
        const target = findSkipTarget(segs, videoFrame, Math.round(fps / 2))
        if (target === 'end') {
          video.pause()
          showToast('End of filtered moments')
        } else if (target != null) {
          video.currentTime = frameToVideoTime(syncRef.current, target)
        }
      }
    }
    const onDurationChange = () => setVideoDuration(video.duration || 0)
    // The metadata may have arrived before this effect ran (cached source,
    // HMR remount) — read what is already known rather than waiting for an
    // event that already fired.
    if (video.duration) setVideoDuration(video.duration)
    video.addEventListener('durationchange', onDurationChange)
    const onLoadedMetadata = () => {
      setVideoDuration(video.duration || 0)
      // Deep-link: seek to the requested frame if present
      if (deepLinkRef.current != null) {
        const frame = deepLinkRef.current
        deepLinkRef.current = null
        video.currentTime = frameToVideoTime(syncRef.current, frame)
        canvasRef.current?.handleLoadedMetadata(frame)
      } else {
        if (syncRef.current.videoGameStartTime > 0) {
          // Default: seek to game start time
          video.currentTime = syncRef.current.videoGameStartTime
        }
        canvasRef.current?.handleLoadedMetadata(null)
      }
    }

    // The DIRECT presigned URL can fail where the proxied route works
    // (a bucket without CORS for this origin, an expired signature).
    // Swap once; a second error stays failed — the fallback failing
    // means the video itself is the problem, and looping would hammer it.
    const onError = () => {
      if (fallbackRef.current) setSrcFailed(true)
    }
    video.addEventListener('error', onError)
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('seeked', onSeeked)
    video.addEventListener('click', onClick)
    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('loadedmetadata', onLoadedMetadata)
    return () => {
      video.removeEventListener('error', onError)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('click', onClick)
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('durationchange', onDurationChange)
    }
  }, [])

  // Keyboard shortcuts (template initializeEventListeners, ~6169).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't handle any hotkeys if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Spacebar to play/pause
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault()
        const video = videoRef.current
        if (!video || !video.src) return
        if (video.paused) void video.play()
        else video.pause()
      }
      // 'v' key to open video sync modal
      else if (e.key === 'v' || e.key === 'V') {
        e.preventDefault()
        setSyncModalOpen(true)
      }
      // 'h' key to toggle spatial control
      else if (e.key === 'h' || e.key === 'H') {
        e.preventDefault()
        spatialHotkeyRef.current()
      }
      // Arrow Left/Right: ONE FRAME (Ben, 2026-09-24 — was 1s). Playback
      // pauses first, because a frame step during playback is invisible.
      // Shift keeps the 5s jump, matching the transport cluster's small
      // one. Frame mapping goes through the sync (stepFrameSequence), so
      // steps land on frame boundaries and cross the halftime gap whole.
      else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault()
        const video = videoRef.current
        if (video && video.src) {
          const dir = e.key === 'ArrowRight' ? 1 : -1
          if (e.shiftKey) {
            video.currentTime = Math.min(
              Math.max(video.currentTime + dir * 5, 0), video.duration)
          } else {
            if (!video.paused) video.pause()
            // Anchored sequence: the element may echo our last seek as a
            // slightly-lower time (media-frame snap-back), and re-deriving
            // from that echo froze the arrow after one press. The anchor
            // makes presses walk game frames 1, 2, 3 regardless; any real
            // playhead move resets it (see stepFrameSequence).
            const anchor = stepFrameSequence(
              syncRef.current, video.currentTime, dir, video.duration,
              stepAnchorRef.current)
            stepAnchorRef.current = anchor
            video.currentTime = anchor.time
          }
        }
      }
      // Arrow Up/Down: next/previous clip — filtered playback only, so the
      // keys keep their defaults otherwise, and never while a filter
      // dropdown is navigating its options with the same keys.
      else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const segs = skipSegmentsRef.current
        const video = videoRef.current
        if (
          segs && segs.length > 0 && video && video.src
          && !target.closest('[role="listbox"], [aria-haspopup="listbox"]')
        ) {
          e.preventDefault()
          const fps = syncRef.current.fps
          const frame = videoTimeToFrame(syncRef.current, video.currentTime)
          const tol = Math.round(fps / 2)
          const dest = e.key === 'ArrowUp'
            ? nextSegmentStart(segs, frame, tol)
            : prevSegmentStart(segs, frame, tol, Math.round(fps * 1.5))
          if (dest != null) video.currentTime = frameToVideoTime(syncRef.current, dest)
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const triggerVideoUpload = () => fileInputRef.current?.click()

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    if (!gameCode) {
      alert('No game loaded — cannot upload video.')
      return
    }

    setUpload({ visible: true, title: 'Uploading video…', percent: 0, status: '0%', failed: false })
    try {
      const client = new VideoAttachClient({
        onProgress: ({ percent, status }) => {
          setUpload((u) => ({
            ...u,
            percent,
            status: `${Math.round(percent)}% — ${status}`,
          }))
        },
      })
      const { videoUrl: newUrl } = await client.upload(file, gameCode)
      setUpload((u) => ({ ...u, title: 'Upload complete', percent: 100, status: '100%' }))
      setTimeout(() => setUpload((u) => ({ ...u, visible: false })), 1200)
      onVideoAttached(newUrl)
    } catch (err) {
      console.error('Video upload failed:', err)
      const message = err instanceof Error ? err.message : 'Unknown error'
      setUpload((u) => ({ ...u, title: 'Upload failed', status: message, failed: true }))
      setTimeout(() => setUpload((u) => ({ ...u, visible: false, failed: false })), 3500)
    }
  }

  return (
    <div className="video-container">
      <video
        id="game-video"
        controls
        crossOrigin="anonymous"
        preload="auto"
        muted
        ref={videoRef}
        // The reference branch routed this through an R2 CORS shim
        // (lib/videoProxy.ts + a `video_proxy` flag on the session). Neither
        // the route nor the flag exists in this repo, so the shim could only
        // ever point the player at a 404 — the real URL is what plays.
        src={(srcFailed && videoUrlFallback ? videoUrlFallback : videoUrl)
          ?? undefined}
        style={{ display: videoUrl ? 'block' : 'none' }}
      />
      {/* Telestration canvas overlay (drawn on top of video by
          TelestrationController; display toggled imperatively) */}
      <canvas
        id="telestration-canvas"
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          pointerEvents: 'none', display: 'none',
        }}
      />
      {/* Annotation canvas overlay (user drawing — pointer-events toggled by
          the active tool in AnnotationController.setTool) */}
      <canvas
        id="annotation-canvas"
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: 'calc(100% - 40px)',
          pointerEvents: 'none', display: 'none', cursor: 'crosshair',
        }}
      />

      {videoUrl && (
        <TransportControls getVideo={() => videoRef.current}
                           paused={videoPaused} />
      )}

      {!videoUrl && (
        <div
          className="video-placeholder"
          id="video-placeholder"
          onClick={triggerVideoUpload}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <i className="ph ph-upload-simple" style={{ fontSize: 36 }} />
            <div>Upload video</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>MP4, MOV, WebM, AVI</div>
          </div>
        </div>
      )}

      {/* Hidden file input for video upload */}
      <input
        type="file"
        id="video-upload-input"
        accept="video/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={onFileChange}
      />

      {/* Upload progress overlay */}
      {upload.visible && (
        <div
          id="video-upload-progress"
          style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.85)', color: 'white', padding: '20px 24px',
            borderRadius: 8, zIndex: 60, minWidth: 260, textAlign: 'center',
          }}
        >
          <div id="video-upload-title" style={{ marginBottom: 10, fontWeight: 500 }}>
            {upload.title}
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', height: 6, borderRadius: 3, overflow: 'hidden' }}>
            <div
              id="video-upload-bar"
              style={{
                background: upload.failed ? '#ef4444' : '#3b82f6',
                height: '100%',
                width: `${upload.percent}%`,
                transition: 'width 0.2s',
              }}
            />
          </div>
          <div id="video-upload-status" style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
            {upload.status}
          </div>
        </div>
      )}

      {/* Match Clock Overlay */}
      <div className="match-clock" id="video-match-clock" ref={clockRef}>00:00</div>
      {scoreline && (
        <div className="match-clock match-clock--score"
             title="Score at this point, from confirmed goals">{scoreline}</div>
      )}
      {showFrameCounter && (
        <div className="match-clock match-clock--frame" id="video-match-frame" ref={frameRef}>f0</div>
      )}

      {/* Video overlay actions (top-right, aligned with match clock).
          Hidden on request — but never while the pane is popped out, since
          the button that brings it back lives in here. */}
      <div
        className="video-overlay-actions"
        style={showActions || poppedOut ? undefined : { display: 'none' }}
      >
        {onPopOut && (
          <button
            className={`video-overlay-btn${poppedOut ? ' active' : ''}`}
            onClick={onPopOut}
            title={poppedOut ? 'Put the video back' : 'Open the video in its own window'}
          >
            <i className={`ph ${poppedOut ? 'ph-arrow-square-in' : 'ph-arrow-square-out'}`} />
          </button>
        )}
        <button
          className="video-overlay-btn"
          onClick={() => onShareCurrentFrame(getCurrentFrame())}
          title="Copy link to current frame"
        >
          <i className="ph ph-share-network" />
        </button>
        <button
          className={`video-overlay-btn${markMoment.active ? ' active' : ''}`}
          id="mark-moment-btn"
          title="Mark a moment"
          onClick={toggleMarkMoment}
        >
          <i className="ph ph-scissors" />
        </button>
        <button
          className="video-overlay-btn"
          id="replace-video-btn"
          onClick={triggerVideoUpload}
          title="Replace video"
          style={{ display: videoUrl ? undefined : 'none' }}
        >
          <i className="ph ph-upload-simple" />
        </button>
        <button
          className="video-overlay-btn"
          onClick={() => setSyncModalOpen(true)}
          title="Video sync settings"
        >
          <i className="ph ph-gear" />
        </button>
        {/* Eye icon only meaningful on TRDaaS games (where the controls are
            available to toggle). Hidden otherwise. */}
        <button
          className="video-overlay-btn"
          id="tel-controls-toggle"
          title="Show / hide telestration controls"
          style={{ display: hasTelestration ? 'inline-flex' : 'none' }}
          onClick={toggleTelControlsVisibility}
        >
          <i
            className={`ph ${telControlsVisible ? 'ph-eye' : 'ph-eye-slash'}`}
            id="tel-controls-toggle-icon"
          />
        </button>
      </div>

      {/* Telestration toggle buttons — shown only when homography is
          available (TRDaaS games); hidden while annotation mode is active
          (its toolbar takes over). QA possession/chain banner buttons are
          deferred with the QA banners. */}
      <div
        className="telestration-controls"
        id="telestration-controls"
        style={{
          position: 'absolute', top: 85, right: 10, zIndex: 20,
          flexDirection: 'column', gap: 4,
          display: hasTelestration && telControlsVisible && !annotationMode ? 'flex' : 'none',
        }}
      >
        <button
          className={`telestration-btn${telLayers['offside_line'] ? ' active' : ''}`}
          id="tel-offside"
          title="Show offside lines"
          onClick={(e) => {
            canvas?.telestration.toggleTelestration('offside_line')
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-line-segments" /> Offside
        </button>
        {/* Rewired in the template (~9618): enters full annotation mode with
            the player-connect overlay, or exits it. */}
        <button
          className={`telestration-btn${telLayers['connect'] ? ' active' : ''}`}
          id="tel-connect"
          title="Telestrate on pitch"
          onClick={(e) => {
            canvas?.annotations.telestrateButtonClick()
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-graph" /> Telestrate
        </button>
        <div style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '2px 0' }} />
        <button
          className={`telestration-btn${telLayers['backline'] ? ' active' : ''}`}
          id="tel-backline"
          title="Show defensive line"
          onClick={(e) => {
            canvas?.telestration.toggleAutoTelestration('backline')
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-shield" /> Back Line
        </button>
        <button
          className={`telestration-btn${telLayers['midfield'] ? ' active' : ''}`}
          id="tel-midfield"
          title="Show midfield line"
          onClick={(e) => {
            canvas?.telestration.toggleAutoTelestration('midfield')
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-dots-three" /> Midfield
        </button>
        <button
          className={`telestration-btn${telLayers['shape'] ? ' active' : ''}`}
          id="tel-shape"
          title="Show defensive shape"
          onClick={(e) => {
            canvas?.telestration.toggleAutoTelestration('shape')
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-polygon" /> Shape
        </button>
        <button
          className={`telestration-btn${telLayers['space'] ? ' active' : ''}`}
          id="tel-space"
          title="Show gaps between defenders"
          onClick={(e) => {
            canvas?.telestration.toggleAutoTelestration('space')
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-arrows-out" /> Space
        </button>
        {showDetectionBtn && (
          <>
            <div style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '2px 0' }} />
            <button
              className={`telestration-btn${detectionActive ? ' active' : ''}`}
              id="tel-detection"
              title="Show TRDaaS detection bounding boxes"
              onClick={(e) => {
                canvas?.telestration.toggleDetectionBoxes()
                e.currentTarget.blur()
              }}
            >
              <i className="ph ph-bounding-box" /> Detection
            </button>
            {highlightMode === 'off' && (
              <button
                className="telestration-btn"
                id="tel-highlight"
                title="Click players to select them, then Done dims everyone else"
                onClick={(e) => {
                  canvas?.telestration.startHighlightSelect()
                  e.currentTarget.blur()
                }}
              >
                <i className="ph ph-user-focus" /> Highlight
              </button>
            )}
            {highlightMode === 'selecting' && (
              <>
                <button
                  className={`telestration-btn${highlightBall ? ' active' : ''}`}
                  id="tel-highlight-ball"
                  title="Include the ball in the highlight (projected from tracking)"
                  onClick={(e) => {
                    canvas?.telestration.toggleHighlightBall()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-soccer-ball" /> Ball
                </button>
                <button
                  className="telestration-btn active"
                  id="tel-highlight-done"
                  title="Finish selecting and dim everyone else"
                  onClick={(e) => {
                    canvas?.telestration.finishHighlightSelect()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-check" /> Done
                </button>
                <button
                  className="telestration-btn"
                  id="tel-highlight-cancel"
                  title="Cancel the selection"
                  onClick={(e) => {
                    canvas?.telestration.clearHighlight()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-x" /> Cancel
                </button>
              </>
            )}
            {highlightMode === 'active' && (
              <>
                <button
                  className="telestration-btn active"
                  id="tel-highlight-clear"
                  title="Remove the highlight"
                  onClick={(e) => {
                    canvas?.telestration.clearHighlight()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-user-focus" /> Unhighlight
                </button>
                <button
                  className={`telestration-btn${highlightLinesVideo ? ' active' : ''}`}
                  id="tel-highlight-lines-video"
                  title="Draw connecting lines on the video"
                  onClick={(e) => {
                    canvas?.telestration.toggleHighlightLinesVideo()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-line-segments" /> Lines
                </button>
                <button
                  className={`telestration-btn${highlightRingsSpatial ? ' active' : ''}`}
                  id="tel-highlight-rings-spatial"
                  title="Gold rings on the pitch view"
                  onClick={(e) => {
                    canvas?.telestration.toggleHighlightRingsSpatial()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-circle" /> Rings
                </button>
                <button
                  className={`telestration-btn${highlightLinesSpatial ? ' active' : ''}`}
                  id="tel-highlight-lines-spatial"
                  title="Connecting lines on the pitch view"
                  onClick={(e) => {
                    canvas?.telestration.toggleHighlightLinesSpatial()
                    e.currentTarget.blur()
                  }}
                >
                  <i className="ph ph-polygon" /> Pitch lines
                </button>
              </>
            )}
          </>
        )}
        <div style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '2px 0' }} />
        <button
          className="telestration-btn telestration-btn--clear"
          id="tel-clear"
          title="Clear all overlays"
          onClick={(e) => {
            canvas?.telestration.clearTelestration()
            e.currentTarget.blur()
          }}
        >
          <i className="ph ph-x" /> Clear
        </button>
      </div>

      {/* Mark-a-moment panel (template built this DOM in toggleMarkMoment) */}
      {markMoment.active && (
        <div className="mark-moment-panel" id="mark-moment-panel">
          <label>Start</label>
          <div className="mark-moment-panel__row">
            <input
              type="text"
              id="mm-start"
              value={mmStart}
              placeholder="MM:SS"
              onChange={(e) => setMmStart(e.target.value)}
            />
            <button className="mark-moment-panel__set-btn" onClick={setMomentStart}>
              Set to now
            </button>
          </div>
          <label>End</label>
          <div className="mark-moment-panel__row">
            <input
              type="text"
              id="mm-end"
              value={mmEnd}
              placeholder="MM:SS"
              onChange={(e) => setMmEnd(e.target.value)}
            />
            <button className="mark-moment-panel__set-btn" onClick={setMomentEnd}>
              Set to now
            </button>
          </div>
          <label>Title</label>
          <input
            type="text"
            id="mm-title"
            placeholder="e.g. Great press from #14"
            value={mmTitle}
            onChange={(e) => setMmTitle(e.target.value)}
          />
          <div className="mark-moment-panel__buttons">
            <button className="mark-moment-panel__cancel" onClick={cancelMarkMoment}>
              Cancel
            </button>
            <button className="mark-moment-panel__save" onClick={() => void saveManualMoment()}>
              Save
            </button>
          </div>
        </div>
      )}

      {syncModalOpen && (
        <VideoSyncModal
          gameCode={gameCode}
          gameData={gameData}
          sync={sync}
          onClose={() => setSyncModalOpen(false)}
          onSaved={(vals) => {
            onSyncSaved(vals)
            // Seek video to the new game-start time if it's loaded
            const video = videoRef.current
            if (video && video.readyState >= 1) {
              video.currentTime = vals.video_game_start_time
            }
            setSyncModalOpen(false)
          }}
        />
      )}
      {/* Match structure: KO, HT, second-half KO and FT, overlaid on the
          bottom edge of the video just below the native seek bar, positioned
          by the same time fraction it uses. Each chip jumps to its mark.
          Rendered only when a mark exists (0 reads as unset: the ingest
          writes 0 for never measured). Shown with the rest of the video
          chrome on hover, hidden when the controls hide. */}
      {videoUrl && videoDuration > 0 && (() => {
      const marks = structureMarks(sync, gameData?.video_normaltime_end_time, videoDuration)
      if (!marks.length) return null
      return (
        <div className={`timeline-marks${videoPaused ? ' timeline-marks--pinned' : ''}`}>
          {marks.map((m) => {
            const pct = (m.t / videoDuration) * 100
            const anchor = pct < 4 ? 'left' : pct > 96 ? 'right' : m.anchor
            const edge = anchor ? ` timeline-marks__chip--${anchor}` : ''
            return (
              <div key={m.label} className="timeline-marks__at" style={{ left: `${pct}%` }}>
                <button
                  className={`timeline-marks__chip${edge}`}
                  title={`Skip to ${m.title.toLowerCase()}`}
                  onClick={(e) => {
                    const video = videoRef.current
                    if (video) video.currentTime = m.t
                    e.currentTarget.blur()
                  }}
                >
                  {m.label}
                </button>
              </div>
            )
          })}
        </div>
      )
      })()}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Video Sync Modal (template ~1896-1953 markup, ~7623-7695 logic)
// ---------------------------------------------------------------------------

interface SyncVals {
  video_game_start_time: number
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_normaltime_end_time: number | null
  /** Only sent when the reviewer chose one — an unset game must not save
   *  as 'right' because the timing fields were touched (RULED B). */
  home_team_start_direction?: string
}

function VideoSyncModal({
  gameCode, gameData, sync, onClose, onSaved,
}: {
  gameCode: string
  gameData: GameData | null
  sync: SyncState
  onClose: () => void
  onSaved: (vals: SyncVals) => void
}) {
  // Pre-fill from gameData (loaded from /game-match-summary/api/game/<code>)
  const [gameStart, setGameStart] = useState<string>(
    String(gameData?.video_game_start_time ?? sync.videoGameStartTime ?? 0),
  )
  const [halftimeStart, setHalftimeStart] = useState<string>(
    gameData?.video_halftime_start_time != null ? String(gameData.video_halftime_start_time) : '',
  )
  const [halftimeEnd, setHalftimeEnd] = useState<string>(
    gameData?.video_halftime_end_time != null ? String(gameData.video_halftime_end_time) : '',
  )
  const [normaltimeEnd, setNormaltimeEnd] = useState<string>(
    gameData?.video_normaltime_end_time != null ? String(gameData.video_normaltime_end_time) : '',
  )
  // '' = not recorded: neither radio pre-checked, so nobody confirms a
  // direction the data does not hold (ledger 26, RULED B).
  const [direction, setDirection] = useState<string>(
    gameData?.home_team_start_direction ?? '',
  )

  // Staff facts view: game_facts team identity, read-only until unlocked.
  const session = useSession()
  const isStaff = session.user.is_staff || session.user.is_platform_admin
  const [identity, setIdentity] = useState<TeamIdentity | null>(
    gameData?.identity ?? null,
  )
  const [editIdentity, setEditIdentity] = useState(false)
  const [homeName, setHomeName] = useState(identity?.home_team_name ?? '')
  const [awayName, setAwayName] = useState(identity?.away_team_name ?? '')
  const [orgSide, setOrgSide] = useState(identity?.org_side ?? '')
  const [homeIdx, setHomeIdx] = useState(
    identity?.tracking_home_index != null ? String(identity.tracking_home_index) : '',
  )

  const saveIdentity = async () => {
    try {
      const response = await fetch(`/api/v1/video/update-team-identity/${gameCode}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          home_team_name: homeName.trim() || null,
          away_team_name: awayName.trim() || null,
          org_side: orgSide || null,
          tracking_home_index: homeIdx === '' ? null : Number(homeIdx),
        }),
      })
      const data = await response.json()
      if (!data.success) {
        alert('Failed to save team identity: ' + (data.error || 'Unknown error'))
        return
      }
      setIdentity(data.identity as TeamIdentity)
      setEditIdentity(false)
    } catch (err) {
      console.error('Error saving team identity:', err)
      alert('Error saving team identity. Check console for details.')
    }
  }

  const save = async () => {
    const vals: SyncVals = {
      video_game_start_time: parseFloat(gameStart) || 0,
      video_halftime_start_time: halftimeStart ? parseFloat(halftimeStart) : null,
      video_halftime_end_time: halftimeEnd ? parseFloat(halftimeEnd) : null,
      video_normaltime_end_time: normaltimeEnd ? parseFloat(normaltimeEnd) : null,
    }
    if (direction === 'left' || direction === 'right') {
      vals.home_team_start_direction = direction
    }
    try {
      const response = await fetch(`/api/v1/video/update-sync-times/${gameCode}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vals),
      })
      const data = await response.json()
      if (!data.success) {
        alert('Failed to save settings: ' + (data.error || 'Unknown error'))
        return
      }
      onSaved(vals)
    } catch (err) {
      console.error('Error saving video sync settings:', err)
      alert('Error saving settings. Check console for details.')
    }
  }

  return (
    <div
      id="video-sync-modal"
      className="modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal-content" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>Video Sync Settings</h3>
          <button className="modal-close" onClick={onClose} style={{ cursor: 'pointer' }}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p className="help-text">
            Set the timestamps in the video where game events occur to sync with tracking data.
          </p>

          <div className="form-group">
            <label htmlFor="video-game-start-time">Game Start Time (seconds)</label>
            <input
              type="number"
              id="video-game-start-time"
              step="0.1"
              min="0"
              placeholder="0.0"
              value={gameStart}
              onChange={(e) => setGameStart(e.target.value)}
            />
            <small>When the match actually begins in the video</small>
          </div>

          <div className="form-group">
            <label htmlFor="video-halftime-start">Halftime Start (seconds)</label>
            <input
              type="number"
              id="video-halftime-start"
              step="0.1"
              min="0"
              placeholder="Leave empty if not applicable"
              value={halftimeStart}
              onChange={(e) => setHalftimeStart(e.target.value)}
            />
            <small>When the first half ends</small>
          </div>

          <div className="form-group">
            <label htmlFor="video-halftime-end">Halftime End (seconds)</label>
            <input
              type="number"
              id="video-halftime-end"
              step="0.1"
              min="0"
              placeholder="Leave empty if not applicable"
              value={halftimeEnd}
              onChange={(e) => setHalftimeEnd(e.target.value)}
            />
            <small>When the second half begins</small>
          </div>

          <div className="form-group">
            <label htmlFor="video-normaltime-end">Full Time End (seconds)</label>
            <input
              type="number"
              id="video-normaltime-end"
              step="0.1"
              min="0"
              placeholder="Leave empty if not applicable"
              value={normaltimeEnd}
              onChange={(e) => setNormaltimeEnd(e.target.value)}
            />
            <small>When regular time ends (90 minutes)</small>
          </div>

          <hr style={{ margin: '20px 0' }} />

          <div className="form-group">
            <label>Home Team Start Direction</label>
            <small style={{ display: 'block', marginBottom: 10 }}>
              Which direction does the home team attack in the first half?
            </small>
            <div style={{ display: 'flex', gap: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="home-team-direction"
                  value="right"
                  id="home-team-right"
                  style={{ marginRight: 8 }}
                  checked={direction === 'right'}
                  onChange={() => setDirection('right')}
                />
                <span>Attacking Right →</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="home-team-direction"
                  value="left"
                  id="home-team-left"
                  style={{ marginRight: 8 }}
                  checked={direction === 'left'}
                  onChange={() => setDirection('left')}
                />
                <span>← Attacking Left</span>
              </label>
            </div>
            <small style={{ marginTop: 8, display: 'block' }}>
              This determines field zone calculations (defensive/attacking thirds)
            </small>
          </div>

          {isStaff && (
            <>
              <hr style={{ margin: '20px 0' }} />
              <div className="form-group" data-testid="team-identity-section">
                <label>Team Identity <small>(game_facts · Priori staff)</small></label>
                {!editIdentity ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                                  gap: '4px 16px', margin: '8px 0' }}>
                      <small>Home team</small>
                      <span>{identity?.home_team_name ?? 'not recorded'}</span>
                      <small>Away team</small>
                      <span>{identity?.away_team_name ?? 'not recorded'}</span>
                      <small>Our side</small>
                      <span>{identity?.org_side ?? 'not recorded'}</span>
                      <small>Tracking home index</small>
                      <span>{identity?.tracking_home_index ?? 'not recorded'}</span>
                      <small>Source</small>
                      <span>{identity?.source ?? '—'}</span>
                    </div>
                    <button className="btn btn-secondary" style={{ cursor: 'pointer' }}
                            onClick={() => setEditIdentity(true)}>
                      Edit
                    </button>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="identity-home-name">Home team name</label>
                      <input id="identity-home-name" type="text" maxLength={120}
                             value={homeName}
                             onChange={(e) => setHomeName(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="identity-away-name">Away team name</label>
                      <input id="identity-away-name" type="text" maxLength={120}
                             value={awayName}
                             onChange={(e) => setAwayName(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="identity-org-side">Our side</label>
                      <select id="identity-org-side" value={orgSide}
                              onChange={(e) => setOrgSide(e.target.value)}>
                        <option value="">not recorded</option>
                        <option value="home">home</option>
                        <option value="away">away</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="identity-home-idx">Tracking home index</label>
                      <select id="identity-home-idx" value={homeIdx}
                              onChange={(e) => setHomeIdx(e.target.value)}>
                        <option value="">not recorded</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                      </select>
                      <small>Which tracking team index (0/1) is the home side</small>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary" style={{ cursor: 'pointer' }}
                              onClick={() => setEditIdentity(false)}>
                        Cancel
                      </button>
                      <button className="btn btn-primary" style={{ cursor: 'pointer' }}
                              onClick={() => void saveIdentity()}>
                        Save Identity
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} style={{ cursor: 'pointer' }}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} style={{ cursor: 'pointer' }}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  )
}
